"use client"

import Link from "next/link"
import { useI18n } from "@/lib/i18n/context"
import { LanguageSelector } from "@/components/public/language-selector"

export default function PublicFooter() {
  const { t } = useI18n()

  return (
    <footer className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
      <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 justify-between">
        <div className="lg:w-1/3 space-y-5">
          <img src="/zindra_black.svg" alt="Zindra" className="h-6" />
          <p className="text-sm text-zinc-500 max-w-sm leading-relaxed">
            ERP de gestion completa para negocios de hosteleria. Todo lo que necesitas en una sola plataforma.
          </p>
        </div>
        <div className="flex flex-wrap gap-10 lg:gap-20 text-sm">
          <div className="space-y-4">
            <h3 className="text-xs uppercase font-semibold tracking-wider text-zinc-400">Producto</h3>
            <ul className="space-y-3 text-zinc-600">
              <li><Link href="/facturacion" className="hover:text-zinc-900 transition-colors">Facturacion</Link></li>
              <li><Link href="/empleados" className="hover:text-zinc-900 transition-colors">Personal</Link></li>
              <li><Link href="/reporting" className="hover:text-zinc-900 transition-colors">Reporting</Link></li>
              <li><Link href="/produccion" className="hover:text-zinc-900 transition-colors">Produccion</Link></li>
              <li><Link href="/tostadores" className="hover:text-zinc-900 transition-colors">Tostadores de café</Link></li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-xs uppercase font-semibold tracking-wider text-zinc-400">Recursos</h3>
            <ul className="space-y-3 text-zinc-600">
              <li><Link href="/plantillas" className="hover:text-zinc-900 transition-colors">Plantillas</Link></li>
              <li><Link href="/mejores" className="hover:text-zinc-900 transition-colors">Comparativas</Link></li>
              <li><Link href="/glosario" className="hover:text-zinc-900 transition-colors">Glosario</Link></li>
              <li><Link href="/calculadora" className="hover:text-zinc-900 transition-colors">Calculadoras</Link></li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-xs uppercase font-semibold tracking-wider text-zinc-400">Empresa</h3>
            <ul className="space-y-3 text-zinc-600">
              <li><Link href="/sobre-nosotros" className="hover:text-zinc-900 transition-colors">Sobre Nosotros</Link></li>
              <li><Link href="/contacto" className="hover:text-zinc-900 transition-colors">Contacto</Link></li>
              <li><Link href="/affiliate-login" className="hover:text-zinc-900 transition-colors">Afiliacion</Link></li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-xs uppercase font-semibold tracking-wider text-zinc-400">Legal</h3>
            <ul className="space-y-3 text-zinc-600">
              <li><Link href="/legal/privacidad" className="hover:text-zinc-900 transition-colors">Privacidad</Link></li>
              <li><Link href="/legal/terminos" className="hover:text-zinc-900 transition-colors">Terminos</Link></li>
              <li><Link href="/legal/cookies" className="hover:text-zinc-900 transition-colors">Cookies</Link></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-12 pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-6 text-xs border-zinc-200 text-zinc-400">
        <p>© {new Date().getFullYear()} Zindra. Todos los derechos reservados.</p>
        <div className="flex items-center gap-6">
          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a
              href="https://twitter.com/zindraapp"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-zinc-600 transition-colors"
              aria-label="X (Twitter)"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span className="hidden sm:inline">X</span>
            </a>
            <a
              href="https://linkedin.com/company/zindra"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-zinc-600 transition-colors"
              aria-label="LinkedIn"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              <span className="hidden sm:inline">LinkedIn</span>
            </a>
            <a
              href="https://instagram.com/zindraapp"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-zinc-600 transition-colors"
              aria-label="Instagram"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
              </svg>
              <span className="hidden sm:inline">Instagram</span>
            </a>
          </div>
          <div className="h-4 w-px bg-zinc-300" />
          <LanguageSelector />
        </div>
      </div>
    </footer>
  )
}
